import React, { useState, useEffect } from "react";
import { Box, Button, Stack, Typography, Checkbox, Divider, TextField } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useSnackbar } from "notistack";
import { useDispatch, useSelector } from 'react-redux';
import { saveUser } from "../../../redux/reducers/userSlice";
import CustomIcon from "../../customIcon";
import AreYouSureModal from "../areYouSureModal";
import userServices from "../../../services/userServices";
import authServices from "../../../services/authServices";
import familymemberServices from "../../../services/familymemberServices";
import logsService from "../../../services/logServices";
import { InvestorActivityEventType } from "../../../utils/eventTypes";

/**
 * Shared Blockchain Account Creation Component
 * Supports multiple entity types: INVESTOR, FAMILYMEMBER, HUF, TRUST
 */
export const SharedBlockChainAccCreation = ({
  // Entity data - can be user, familymemberAccount, huf, or trust
  entity,
  entityType = "INVESTOR", // "INVESTOR" | "FAMILYMEMBER" | "HUF" | "TRUST"

  // Callbacks
  customHandleClose,
  setAnimating,

  // State setters (for parent component state tracking)
  setBlockchainAccountDownload,
  blockchainAccountCreated,
  setBlockchainAccountCreated,

  // Optional callbacks for entity-specific actions
  onAccountCreated, // Called after account creation (e.g., getallFamilyMember, getHufDetails, getTrustDetails)
  onAccountDownloaded, // Called after account download

  // UI customization
  nicknamePlaceholder = "Secondary Account",
  disableAlgorandOption = true, // Algorand temporarily disabled for business reasons
  disablePolygonOption = false,
  disableMstOption = false,

  // Additional props for specific entity types
  sendMail,
  setSendMail,
  sendToFamilyMember,
  setSendToFamilyMember,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const user = useSelector(state => state.user.data);

  const getDefaultNetwork = () => {
    if (!disablePolygonOption) return "polygon";
    if (!disableMstOption) return "mst";
    return "polygon";
  };

  // Internal state management
  const [mnemonic, setmnemonic] = useState();
  const [publicAddress, setPublicAddress] = useState();
  const [encryptedMnemonic, setEncryptedMnemonic] = useState("");
  const [loading, setLoading] = useState(false);
  const [keyDownloadButtonTxt, setKeyDownloadButtonTxt] = useState('Download Key Details');
  const [dltName, setDltName] = useState('');
  const [confirmModal, setConfirmModal] = useState(false);
  const [availableBlockchainAccounts, setAvailableBlockchainAccounts] = useState([]);
  const [polygonOption, setPolygonOption] = useState("import");
  const [walletAddress, setWalletAddress] = useState("");
  const [privateAddress, setPrivateAddress] = useState("");
  const [network, setNetwork] = useState(getDefaultNetwork());
  const [polygonAccountDownloaded, setPolygonAccountDownloaded] = useState(false);
  const [showPolygonCreate, setShowPolygonCreate] = useState(false);
  const [networkTabs, setNetworkTabs] = useState([]);
  // Get entity ID based on entity type
  const getEntityId = () => {
    if (!entity) return null;
    if (entityType === "INVESTOR") return entity._id;
    if (entityType === "FAMILYMEMBER") {
      // Handle both cases: entity can be an ID string or an object with _id
      return typeof entity === 'string' ? entity : entity._id;
    }
    if (entityType === "HUF") {
      // Handle both cases: entity can be an ID string or an object with _id
      return typeof entity === 'string' ? entity : entity._id;
    };
    if (entityType === "TRUST") {
      // Handle both cases: entity can be an ID string or an object with _id
      return typeof entity === 'string' ? entity : entity._id;
    };
    return entity._id;
  };

  // Get wallet info for secondary account check
  const getWalletInfo = () => {
    if (!entity) return null;
    return entity.walletInfo;
  };

  // Fetch DLT wallet
  const fetchDltWallet = async () => {
    const entityId = getEntityId();
    if (!entityId) return;

    try {
      let dltWallet = await userServices.getDltWallet(entityId);
      setAvailableBlockchainAccounts(dltWallet.data || []);
    } catch (error) {
      console.error("Error fetching DLT wallet:", error);
    }
  };

  useEffect(() => {
    fetchDltWallet();
    setNetwork(getDefaultNetwork());
  }, [entity,disableMstOption]);

  const handleNetworkChange = (nextNetwork) => {
    setNetwork(nextNetwork);
    setWalletAddress("");
    setShowPolygonCreate(false);
    setPolygonOption("import");
  };
const validateNickname = () => {
    if (!dltName.trim()) {
        enqueueSnackbar("Nickname is required", {
            variant: "error",
            autoHideDuration: 4000,
        });
        return false;
    }

    return true;
};

  const createDlt = () => {
    if (dltName === "") {
      setConfirmModal(true);
      return;
    }
    createBlockChainAcc();
  };

  const createBlockChainAcc = async () => {
    const entityId = getEntityId();
    if (!entityId) return;

    let data = {
      entityType: entityType,
      entityId: entityId,
    };

    setLoading(true);
    try {
      let res = await userServices.createPublicAddressAndMemonic(data);
      setLoading(false);

      if (res && res.success) {
        setAnimating(true);
        setmnemonic(res?.mnemonic);
        setPublicAddress(res?.publicAddress);
        setBlockchainAccountCreated(true);

        // Log events for specific entity types
        if (entityType === "HUF" && user?._id) {
          logsService.addLog({
            userId: user._id,
            event: InvestorActivityEventType.HUF_DLT_ACCOUNT_CREATED
          });
        }
        if (entityType === "TRUST" && user?._id) {
          logsService.addLog({
            userId: user._id,
            event: InvestorActivityEventType.TRUST_DLT_ACCOUNT_CREATED
          });
        }

        // Call optional callback
        if (onAccountCreated) {
          onAccountCreated();
        }
      } else {
        enqueueSnackbar(res?.data?.message || "Something went wrong!!!", { variant: "error", autoHideDuration: 5000 });
      }
    } catch (error) {
      setLoading(false);
      enqueueSnackbar("Something went wrong!!!", { variant: "error", autoHideDuration: 5000 });
    }
  };

  const handlePolygonCreate = async (optionOverride) => {

   if (!validateNickname()) {
    return;
}

    const entityId = getEntityId();
    if (!entityId) return;
    const selectedOption = optionOverride || polygonOption;
    const coupon = localStorage.getItem("coupon");

    setLoading(true);
    try {
      let data = {
        entityType: entityType,
        entityId: entityId,
        polygonOption: selectedOption,
        publicAddress: walletAddress,
        dltName: dltName,
        coupon: coupon ? coupon : null
      };
      const walletInfo = getWalletInfo();
      // For secondary accounts: check if there's already at least one account
      // and user is approved to create DLT
      if (availableBlockchainAccounts.length >= 1 && walletInfo?.isAllowedToCreateDLT === 'APPROVED') {
        data.isSecondaryAccount = true;
      }
      let res = await userServices.createPublicAddressAndMemonicForPolygon(data);
      setLoading(false);

      if (res && res.success) {
        if (selectedOption === "import") {
          setAnimating(true);
          enqueueSnackbar(res?.data?.message || "Polygon Account Imported", { variant: "success", autoHideDuration: 5000 });
          if (entityType === "INVESTOR") {
            dispatch(saveUser(res.data));
          }
          customHandleClose();
        } else {
          //   setAnimating(true);
          setmnemonic(res?.mnemonic);
          setPublicAddress(res?.publicAddress);
          setPrivateAddress(res?.privateKey);
          setBlockchainAccountCreated(true);

          // Log events for specific entity types
          if (entityType === "HUF" && user?._id) {
            logsService.addLog({
              userId: user._id,
              event: InvestorActivityEventType.HUF_DLT_ACCOUNT_CREATED
            });
          }
          if (entityType === "TRUST" && user?._id) {
            logsService.addLog({
              userId: user._id,
              event: InvestorActivityEventType.TRUST_DLT_ACCOUNT_CREATED
            });
          }
          if (entityType === "FAMILYMEMBER") {
            logsService.addLog({
              userId: user._id,
              event: InvestorActivityEventType.FAMILY_MEMBER_DLT_ACCOUNT_CREATED
            });
          }
          if (entityType === "INVESTOR") {
            logsService.addLog({
              userId: user._id,
              event: InvestorActivityEventType.INVESTOR_DLT_ACCOUNT_CREATED
            });
          }

          // Call optional callback
          if (typeof onAccountCreated === "function") {
            onAccountCreated();
          }
        }
      } else {
        enqueueSnackbar(res?.message || res?.data?.message || "Something went wrong!!!", { variant: "error", autoHideDuration: 5000 });
      }
    } catch (error) {
      setLoading(false);
      console.log("error", error);
      enqueueSnackbar(error.response?.data?.data?.message || error?.data?.message || "Something went wrong!!!", { variant: "error", autoHideDuration: 5000 });
    }
  };

  const handleMstImport = async () => {
    if (!validateNickname()) {
    return;
}
    
    const entityId = getEntityId();
    if (!entityId) return;
    if (!walletAddress?.trim()) {
      enqueueSnackbar("Please enter your MST account address", { variant: "error", autoHideDuration: 4000 });
      return;
    }

    const coupon = localStorage.getItem("coupon");
    setLoading(true);
    try {
      let data = {
        entityType: entityType,
        entityId: entityId,
        publicAddress: walletAddress.trim(),
        dltName: dltName,
        coupon: coupon ? coupon : null,
      };
      const walletInfo = getWalletInfo();
      if (availableBlockchainAccounts.length >= 1 && walletInfo?.isAllowedToCreateDLT === "APPROVED") {
        data.isSecondaryAccount = true;
      }

      const res = await userServices.importDLTMSTAccount(data);
      setLoading(false);

      if (res && res.success) {
        setAnimating(true);
        enqueueSnackbar(res?.data?.message || "MST Account Imported", { variant: "success", autoHideDuration: 5000 });
        if (entityType === "INVESTOR") {
          dispatch(saveUser(res.data));
        }
        if (typeof onAccountCreated === "function") {
          onAccountCreated();
        }
        customHandleClose();
      } else {
        enqueueSnackbar(res?.message || res?.data?.message || "Something went wrong!!!", { variant: "error", autoHideDuration: 5000 });
      }
    } catch (error) {
      setLoading(false);
      enqueueSnackbar(error.response?.data?.data?.message || error?.data?.message || "Something went wrong!!!", { variant: "error", autoHideDuration: 5000 });
    }
  };

  const copyText = (text) => {
    navigator.clipboard.writeText(text);
    enqueueSnackbar("Copied to clipboard.", { variant: "success" });
  };

  const downloadTxtFile = async () => {
    const element = document.createElement("a");
    const res = await authServices.encryptCredentials({ mnemonic, privateKey: privateAddress });

    if (res && res.success) {
      setEncryptedMnemonic(res.data);
      const file = new Blob([publicAddress + "|" + res.data], { type: 'text/plain' });
      element.href = URL.createObjectURL(file);

      // Generate filename based on entity type
      let filename = `ENCRYPTED_KEY_${entityType}_${dltName || 'PERSONAL'}.txt`;
      if (entityType === "FAMILYMEMBER") {
        filename = `ENCRYPTED_KEY_FAMILY_${dltName || 'FAMILY'}.txt`;
      } else if (entityType === "HUF") {
        filename = `ENCRYPTED_KEY_HUF_${dltName || 'HUF'}.txt`;
      } else if (entityType === "TRUST") {
        filename = `DIGITAL_ASSET_ACCOUNT_${publicAddress}.txt`;
      }

      element.download = filename;
      element.click();
      setBlockchainAccountDownload(true);

      // Call saveAndDownloadDlt for INVESTOR, or onAccountDownloaded for others
      await saveAndDownloadDlt();

      // Send mail if requested
      if (sendMail) {
        await sendAlgorandMnemonicThroughMail();
      }

      // Send mail to family member if requested
      if (sendToFamilyMember && entityType === "FAMILYMEMBER") {
        await sendMailToFamilyMember();
      }

      if (onAccountDownloaded) {
        onAccountDownloaded();
      }
    } else {
      enqueueSnackbar("Something Went Wrong, Please Try Again!", { variant: "error" });
    }
    setKeyDownloadButtonTxt('Key Details Downloaded');
  };

  const saveAndDownloadDlt = async () => {
    const entityId = getEntityId();
    if (!entityId) {
      enqueueSnackbar("Entity ID not found", { variant: "error" });
      return;
    }

    // Prevent duplicate calls
    if (loading) {
      return;
    }

    let data = {
      entityType: entityType,
      entityId: entityId,
      dltName: dltName,
      publicAddress: publicAddress
    };

    const walletInfo = getWalletInfo();
    // For secondary accounts: check if there's already at least one account
    // and user is approved to create DLT
    if (availableBlockchainAccounts.length >= 1 && walletInfo?.isAllowedToCreateDLT === 'APPROVED') {
      data.isSecondaryAccount = true;
    }

    setLoading(true);
    try {
      let res = await userServices.downloadPublicAddressAndMemonic(data);
      setLoading(false);

      if (res && res.success) {
        if (entityType === "INVESTOR") {
          dispatch(saveUser(res.data));
        }

        // Log events
        if (entityType === "HUF" && user?._id) {
          logsService.addLog({
            userId: user._id,
            event: InvestorActivityEventType.HUF_DLT_KEY_DOWNLOADED
          });
        }
        if (entityType === "TRUST" && user?._id) {
          logsService.addLog({
            userId: user._id,
            event: InvestorActivityEventType.TRUST_DLT_KEY_DOWNLOADED
          });
        }

        if (entityType === "FAMILYMEMBER") {
          logsService.addLog({
            userId: user._id,
            event: InvestorActivityEventType.FAMILY_MEMBER_DLT_KEY_DOWNLOADED
          });
        }

        if (entityType === "INVESTOR") {
          logsService.addLog({
            userId: user._id,
            event: InvestorActivityEventType.INVESTOR_DLT_KEY_DOWNLOADED
          });
        }
        if (onAccountDownloaded) {
          onAccountDownloaded();
        }
      } else {
        enqueueSnackbar(res?.data?.message || "Something went wrong!!!", { variant: "error" });
      }
    } catch (error) {
      setLoading(false);
      enqueueSnackbar(error?.response?.data?.message || error?.data?.message || "Something went wrong!!!", { variant: "error" });
    }
  };

  // Polygon download and save functions
  const downloadPolygonTxtFile = async () => {
    const res = await authServices.encryptCredentialsPolygon({ mnemonic, privateKey: privateAddress });
    if (res && res.success) {
      setEncryptedMnemonic(res.data);

      const file = new Blob(
        [publicAddress + "|" + privateAddress + "|" + res.data],
        { type: "text/plain" }
      );

      const element = document.createElement("a");
      element.href = URL.createObjectURL(file);
      element.download = `ENCRYPTED_KEY_POLYGON.txt`;
      element.click();

      setPolygonAccountDownloaded(true);
      setBlockchainAccountDownload(true);
      await saveAndDownloadPolygonDlt();

      // Send mail if requested
      if (sendMail) {
        await sendPolygonMnemonicThroughMail();
      }

      setKeyDownloadButtonTxt("Key Details Downloaded");
    } else {
      enqueueSnackbar("Something Went Wrong, Please Try Again!", {
        variant: "error",
      });
    }
  };
  const saveAndDownloadPolygonDlt = async () => {
    const entityId = getEntityId();
    if (!entityId) {
      enqueueSnackbar("Entity ID not found", { variant: "error" });
      return;
    }

    // Prevent duplicate calls
    if (loading) {
      return;
    }

    const coupon = localStorage.getItem("coupon");
    let data = {
      entityType: entityType ? entityType : "INVESTOR",
      entityId: entityId,
      dltName: dltName,
      publicAddress: publicAddress,
      coupon: coupon ? coupon : null
    };

    const walletInfo = getWalletInfo();
    // For secondary accounts: check if there's already at least one account
    // and user is approved to create DLT
    if (availableBlockchainAccounts.length >= 1 && walletInfo?.isAllowedToCreateDLT === 'APPROVED') {
      data.isSecondaryAccount = true;
    }

    setLoading(true);
    try {
      let res = await userServices.downloadPublicAddressAndMemonicForPolygon(data);
      setLoading(false);
      if (res && res.success) {
        if (entityType === "INVESTOR") {
          dispatch(saveUser(res.data));
        }

        // Log events
        if (entityType === "HUF" && user?._id) {
          logsService.addLog({
            userId: user._id,
            event: InvestorActivityEventType.HUF_DLT_KEY_DOWNLOADED
          });
        }
        if (entityType === "TRUST" && user?._id) {
          logsService.addLog({
            userId: user._id,
            event: InvestorActivityEventType.TRUST_DLT_KEY_DOWNLOADED
          });
        }

        if (onAccountDownloaded) {
          onAccountDownloaded();
        }
      } else {
        enqueueSnackbar(res?.data?.message || "Something went wrong!!!", { variant: "error" });
      }
    } catch (error) {
      setLoading(false);
      enqueueSnackbar(error?.response?.data?.message || error?.data?.message || "Something went wrong!!!", { variant: "error" });
    }
  };

  const sendAlgorandMnemonicThroughMail = async () => {
    if (!publicAddress || !encryptedMnemonic) {
      return;
    }
    const userId = entityType === "INVESTOR" ? entity?._id : user?._id;
    await authServices.sendMnemonicThroughMail({
      data: publicAddress + "|" + encryptedMnemonic,
      mnemonic,
      userId: userId,
      publicAddress: publicAddress,
      ...(entityType === "FAMILYMEMBER" && sendToFamilyMember && { isFamilyMember: true }),
      ...(entityType === "HUF" && { huf: true }),
      ...(entityType === "TRUST" && { trust: true }),
    });
  };

  const sendMailToFamilyMember = async () => {
    if (!publicAddress || !encryptedMnemonic || entityType !== "FAMILYMEMBER") {
      return;
    }
    const entityId = getEntityId();
    if (!entityId) return;

    await familymemberServices.sendMailToFamilyMember({
      data: publicAddress + "|" + encryptedMnemonic,
      mnemonic,
      userId: entityId,
      publicAddress: publicAddress
    });
  };

  const sendPolygonMnemonicThroughMail = async () => {
    if (!publicAddress) {
      enqueueSnackbar("No Polygon account available", {
        variant: "error",
      });
      return;
    }
    const userId = entityType === "INVESTOR" ? entity?._id : user?._id;
    await authServices.sendMnemonicThroughMail({
      data: publicAddress + "|" + encryptedMnemonic,
      mnemonic,
      userId: userId,
      publicAddress,
      ...(entityType === "HUF" && { huf: true }),
      ...(entityType === "TRUST" && { trust: true }),
    });
  };

  // Check if we should show Polygon success screen
  const showPolygonSuccess = blockchainAccountCreated && network === "polygon";
  // Check if we should show Algorand success screen
  const showAlgorandSuccess = blockchainAccountCreated && network === "algorand";

  useEffect(() => {
    setNetworkTabs(
      [
        !disablePolygonOption && { name: "Polygon", id: "polygon" },
        !disableMstOption && { name: "MST", id: "mst" },
      ].filter(Boolean),
    );
  }, [disablePolygonOption, disableMstOption,user]);

  const compactFieldSx = {
    minWidth: "0 !important",
    width: "100%",
    padding: "7px 10px !important",
    fontSize: "12px !important",
    "& .MuiInputBase-root": {
      minWidth: "0 !important",
      width: "100%",
      padding: "7px 10px !important",
      fontSize: "12px !important",
    },
    "& .MuiInputBase-input": {
      fontSize: "12px !important",
      padding: "0 !important",
    },
  };

  const FieldLabel = ({ children }) => (
    <Typography sx={{ fontSize: 10, color: "#9a9a9a", mb: 0.35, textAlign: "left", letterSpacing: 0.2 }}>
      {children}
    </Typography>
  );

  const KeyRow = ({ label, value }) => (
    <Box>
      <Typography sx={{ fontSize: 10, color: "#9a9a9a", mb: 0.35 }}>{label}</Typography>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={{ px: 1, py: 0.85, border: "1px solid", borderColor: "divider", borderRadius: 1, bgcolor: "grey.50" }}
      >
        <Typography sx={{ fontSize: 11, fontFamily: "monospace", wordBreak: "break-all", flex: 1, minWidth: 0, textAlign: "left" }}>
          {value}
        </Typography>
        <Box onClick={() => copyText(value)} sx={{ cursor: "pointer", flexShrink: 0, display: "flex" }}>
          <CustomIcon name="copy" sx={{ width: 14, height: 14, bgcolor: "#333" }} />
        </Box>
      </Stack>
    </Box>
  );

  const WarningNote = ({ children }) => (
    <Box sx={{ bgcolor: "warning.light", px: 1, py: 0.7, borderRadius: 1 }}>
      <Stack direction="row" spacing={0.6} alignItems="flex-start">
        <CustomIcon name="warning" sx={{ bgcolor: "warning.main", width: 12, height: 12, mt: 0.15, flexShrink: 0 }} />
        <Typography sx={{ fontSize: "0.75rem !important", color: "#666", fontWeight: 400, lineHeight: 1.35, textAlign: "left" }}>
          {children}
        </Typography>
      </Stack>
    </Box>
  );

  return (
    <Box sx={{ width: "100%", maxWidth: 440, mx: "auto", px: { xs: 0.5, sm: 1 }, py: 0 }}>
      <AreYouSureModal
        message={"Do you want to save without wallet name?"}
        setOpen={setConfirmModal}
        open={confirmModal}
        handleYes={() => {
          createBlockChainAcc();
        }}
      />

      {showPolygonSuccess ? (
        <Stack spacing={2} alignItems="center">
          <Box
            sx={{
              width: 56,
              height: 56,
              bgcolor: "success.light",
              borderRadius: "50%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CustomIcon name="check" sx={{ bgcolor: "success.main", width: 24, height: 24 }} />
          </Box>
          <Stack spacing={0.5} textAlign="center">
            <Typography sx={{ fontSize: 16, fontWeight: 600 }}>Polygon Account Created</Typography>
            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
              Save the keys below securely. RealX does not store them.
            </Typography>
          </Stack>

          <Stack spacing={1.25} width="100%">
            <KeyRow label="Account ID (Public Key)" value={publicAddress} />
            <KeyRow label="Mnemonic (Seed Phrase)" value={mnemonic} />
            <KeyRow label="Private Key" value={privateAddress} />
            <Button onClick={downloadPolygonTxtFile} variant="contained" size="small" sx={{ fontSize: 12, py: "8px !important" }}>
              {keyDownloadButtonTxt}
            </Button>
          </Stack>

          {sendMail !== undefined && setSendMail && (
            <Stack direction="row" alignItems="flex-start" spacing={0.5} width="100%" sx={{ bgcolor: "grey.50", p: 1, borderRadius: 1 }}>
              <Checkbox checked={sendMail} onClick={() => setSendMail(!sendMail)} size="small" sx={{ p: 0.5 }} />
              <Typography sx={{ fontSize: 11, color: "text.secondary", textAlign: "left", pt: 0.5 }}>
                Send key details to my email
              </Typography>
            </Stack>
          )}

          <Button
            fullWidth
            onClick={() => {
              if (!polygonAccountDownloaded) {
                enqueueSnackbar("Please download key details before closing!", { variant: "error" });
                return;
              }
              customHandleClose();
            }}
            variant="outlined"
            size="small"
            sx={{ fontSize: 12, py: "8px !important" }}
          >
            Complete Account Creation
          </Button>

          <WarningNote>
            Private key and mnemonic are your secure access credentials. RealX cannot recover them for you.
          </WarningNote>
        </Stack>
      ) : showAlgorandSuccess ? (
        <Stack spacing={2} alignItems="center">
          <Box
            sx={{
              width: 56,
              height: 56,
              bgcolor: "success.light",
              borderRadius: "50%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CustomIcon name="check" sx={{ bgcolor: "success.main", width: 24, height: 24 }} />
          </Box>
          <Stack spacing={0.5} textAlign="center">
            <Typography sx={{ fontSize: 16, fontWeight: 600 }}>Digital Assets Account Created</Typography>
            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
              Save the private key below securely. RealX does not store it.
            </Typography>
          </Stack>

          <Stack spacing={1.25} width="100%">
            <KeyRow label="Account ID (Public Key)" value={publicAddress} />
            <KeyRow label="Password (Private Key)" value={mnemonic} />
            <Button onClick={() => downloadTxtFile()} variant="contained" size="small" sx={{ fontSize: 12, py: "8px !important" }}>
              {keyDownloadButtonTxt}
            </Button>
          </Stack>

          {sendMail !== undefined && setSendMail && (
            <Stack direction="row" alignItems="flex-start" spacing={0.5} width="100%" sx={{ bgcolor: "grey.50", p: 1, borderRadius: 1 }}>
              <Checkbox checked={sendMail} onClick={() => setSendMail(!sendMail)} size="small" sx={{ p: 0.5 }} />
              <Typography sx={{ fontSize: 11, color: "text.secondary", textAlign: "left", pt: 0.5 }}>
                Send key details to my email
              </Typography>
            </Stack>
          )}

          <Button fullWidth onClick={() => customHandleClose(publicAddress)} variant="outlined" size="small" sx={{ fontSize: 12, py: "8px !important" }}>
            Complete Account Creation
          </Button>

          <WarningNote>
            The private key is your secure access credential. RealX cannot recover it for you.
          </WarningNote>
        </Stack>
      ) : (
        <Stack spacing={1.5}>
          <Stack spacing={0.5} textAlign="center" alignItems="center">
            <Box
              sx={{
                width: 88,
                height: 88,
                bgcolor: "primary.light",
                borderRadius: "50%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CustomIcon name="blockchain" sx={{ bgcolor: "primary.main", width: 40, height: 40 }} />
            </Box>
            <Typography sx={{ fontSize: 15, fontWeight: 600, color: "#111" }}>
              {entityType !== "INVESTOR" ? `${entityType} DLT Account` : "Add DLT Account"}
            </Typography>
            <Typography sx={{ fontSize: "0.8rem !important", color: "text.secondary", maxWidth: 300 }}>
              Select a network and add your DLT address
            </Typography>
          </Stack>

          <Box sx={{ textAlign: "left" }}>
            {/* <FieldLabel sx={{ fontSize: "0.9rem !important", color: "#111" }}>Nickname</FieldLabel> */}
            <Typography sx={{ fontSize: "0.9rem !important", color: "#111" }}>Nickname <span style={{ color: "red" }}>*</span> </Typography>
            <TextField
              placeholder={nicknamePlaceholder || "Primary Account"}
              type="text"
              value={dltName}
              onChange={(e) => setDltName(e.target.value)}
              fullWidth
              sx={compactFieldSx}
            />
          </Box>

          {networkTabs.length > 1 && (
            <Box sx={{ textAlign: "left" }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: `repeat(${networkTabs.length}, 1fr)`,
                  p: 0.4,
                  borderRadius: 1.5,
                  bgcolor: "#f3f4f6",
                  gap: 0.4,
                }}
              >
                {networkTabs.map((tab) => {
                  const active = network === tab.id;
                  return (
                    <Box
                      key={tab.id}
                      onClick={() => handleNetworkChange(tab.id)}
                      sx={{
                        cursor: "pointer",
                        py: 0.9,
                        px: 1,
                        borderRadius: 1.25,
                        textAlign: "center",
                        bgcolor: active ? "#fff" : "transparent",
                        boxShadow: active ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
                        transition: "all 0.15s ease",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "0.8rem !important",
                          fontWeight: active ? 500 : 400,
                          color: active ? "primary.main" : "#6b7280",
                          lineHeight: 1.2,
                        }}
                      >
                        {tab.name}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          <Divider sx={{ borderColor: "#eee" }} />

          <Box sx={{ textAlign: "left" }}>
            {network === "polygon" && !disablePolygonOption && (
              <Stack spacing={1.25}>
                {!showPolygonCreate ? (
                  <Stack spacing={1}>
                    {/* <Typography sx={{ fontSize: "0.8rem !important", color: "text.secondary", lineHeight: 1.4 }}>
                      Import an existing Polygon wallet, or create a new one.
                    </Typography> */}
                    <Box>
                      {/* <FieldLabel>Polygon account address</FieldLabel> */}
                      <Typography sx={{ fontSize: "0.8rem !important", color: "#111" }}>Polygon account address</Typography>
                      <TextField
                        placeholder="0x1234...abcd"
                        type="text"
                        value={walletAddress}
                        onChange={(e) => setWalletAddress(e.target.value)}
                        fullWidth
                        sx={compactFieldSx}
                      />
                    </Box>
                    <LoadingButton
                      loading={loading && !showPolygonCreate}
                      variant="contained"
                      size="small"
                      onClick={() => handlePolygonCreate("import")}
                      sx={{ fontSize: 12, py: "8px !important", textTransform: "none" }}
                    >
                      Import Polygon Account
                    </LoadingButton>
                    <Typography
                      onClick={() => {
                        setShowPolygonCreate(true);
                        setPolygonOption("create");
                        setWalletAddress("");
                      }}
                      sx={{
                        fontSize: "0.8rem !important",
                        color: "primary.main",
                        cursor: "pointer",
                        textAlign: "center",
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      Or create a new Polygon account
                    </Typography>
                  </Stack>
                ) : (
                  <Stack spacing={1}>
                    <Typography sx={{ fontSize: "0.8rem !important", color: "text.secondary", lineHeight: 1.4 }}>
                      A new Polygon wallet will be created for you.
                    </Typography>
                    <LoadingButton
                      loading={loading}
                      onClick={() => handlePolygonCreate("create")}
                      variant="contained"
                      size="small"
                      sx={{ fontSize: 12, py: "8px !important", textTransform: "none" }}
                    >
                      Create Polygon Account
                    </LoadingButton>
                    <Typography
                      onClick={() => {
                        setShowPolygonCreate(false);
                        setPolygonOption("import");
                      }}
                      sx={{
                        fontSize: "0.8rem !important",
                        color: "text.secondary",
                        cursor: "pointer",
                        textAlign: "center",
                        "&:hover": { color: "primary.main" },
                      }}
                    >
                      Back to import existing wallet
                    </Typography>
                  </Stack>
                )}
              </Stack>
            )}

            {network === "mst" && !disableMstOption && (
              <Stack spacing={1}>
                {/* <Typography sx={{ fontSize: 10, color: "text.secondary", lineHeight: 1.4 }}>
                  Import only — new MST wallets cannot be created here.
                </Typography> */}
                <Box>
                  {/* <FieldLabel>MST account address</FieldLabel> */}
                  <Typography sx={{ fontSize: "0.8rem !important", color: "#111" }}>MST account address</Typography>
                  <TextField
                    placeholder="Enter MST public address"
                    type="text"
                    value={walletAddress}
                    onChange={(e) => setWalletAddress(e.target.value)}
                    fullWidth
                    sx={compactFieldSx}
                  />
                </Box>
                <LoadingButton
                  loading={loading}
                  variant="contained"
                  size="small"
                  onClick={handleMstImport}
                  sx={{ fontSize: 12, py: "8px !important", textTransform: "none" }}
                >
                  Import MST Account
                </LoadingButton>
                <Typography sx={{ fontSize: "0.8rem !important", color: "text.secondary", textAlign: "center", mt: 1 }}>
                  New MST account creation opens soon. Check back later.
                </Typography>
              </Stack>
            )}
          </Box>

          <WarningNote>
            {showPolygonCreate
              ? "Save your keys after creation. RealX does not store them."
              : "Verify the address carefully before importing."}
          </WarningNote>
        </Stack>
      )}
    </Box>
  );
};

export default SharedBlockChainAccCreation;
